import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { History as HistoryIcon, Search } from 'lucide-react';
import { Input } from '../ui/input';
import { MultiCombobox } from '../ui/multi-combobox';
import { DataTable, DataTableColumn } from '../ui/data-table';
import { getHistory } from '../../services/apiService';
import CopyButton from '../general/CopyButton';
import LoadingRing from '../ui-components/LoadingRing';
import Status from '../general-components/Status';
import { toTitleCase } from '../../utils/utils';

export interface HistoryEntry {
    _id: string;
    implantId: string;
    model: string;
    manufacturer: string;
    type: string;
    mriCode: string | null;
    mriStatus: number | null;
    regions: string[];
    viewedAt: string;
}

const History = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [history, setHistory] = useState<HistoryEntry[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
    const [selectedManufacturers, setSelectedManufacturers] = useState<
        string[]
    >([]);

    useEffect(() => {
        const fetchHistory = async () => {
            const entries = await getHistory();
            setHistory(Array.isArray(entries) ? entries : []);
            setIsLoading(false);
        };
        fetchHistory();
    }, []);

    const allTypes = useMemo(
        () =>
            Array.from(
                new Set(history.map((h) => h.type).filter(Boolean))
            ).sort(),
        [history]
    );

    const allManufacturers = useMemo(
        () =>
            Array.from(
                new Set(
                    history
                        .map((h) => toTitleCase(h.manufacturer))
                        .filter(Boolean)
                )
            ).sort(),
        [history]
    );

    const visibleHistory = useMemo(() => {
        let result = history;

        if (selectedTypes.length > 0) {
            result = result.filter((h) => selectedTypes.includes(h.type));
        }

        if (selectedManufacturers.length > 0) {
            result = result.filter((h) =>
                selectedManufacturers.includes(toTitleCase(h.manufacturer))
            );
        }

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            result = result.filter(
                (h) =>
                    h.model?.toLowerCase().includes(term) ||
                    h.manufacturer?.toLowerCase().includes(term) ||
                    h.type?.toLowerCase().includes(term) ||
                    h.mriCode?.toLowerCase().includes(term)
            );
        }

        return result;
    }, [history, searchTerm, selectedTypes, selectedManufacturers]);

    const formatDate = (str: string) =>
        new Date(str).toLocaleString(i18n.language, {
            dateStyle: 'medium',
            timeStyle: 'short',
        });

    const columns: DataTableColumn<HistoryEntry>[] = [
        {
            key: 'model',
            header: t('search.history.table.headers.model'),
            cell: (h) => h.model,
            sortValue: (h) => h.model,
            className: 'font-medium',
        },
        {
            key: 'manufacturer',
            header: t('search.history.table.headers.manufacturer'),
            cell: (h) => toTitleCase(h.manufacturer),
            sortValue: (h) => h.manufacturer,
            className: 'text-muted-foreground',
        },
        {
            key: 'type',
            header: t('search.history.table.headers.type'),
            cell: (h) => h.type,
            sortValue: (h) => h.type,
            className: 'text-muted-foreground',
        },
        {
            key: 'mriCode',
            header: t('search.history.table.headers.mriCode'),
            cell: (h) =>
                h.mriCode ? (
                    <div className="flex items-center gap-1">
                        <span>{h.mriCode}</span>
                        <CopyButton value={h.mriCode} />
                    </div>
                ) : (
                    <span className="text-muted-foreground/50">—</span>
                ),
            sortValue: (h) => h.mriCode,
            className: 'text-muted-foreground',
        },
        {
            key: 'mriStatus',
            header: t('search.history.table.headers.mriStatus'),
            cell: (h) => <Status status={h.mriStatus ?? 0} />,
            sortValue: (h) => h.mriStatus ?? 0,
        },
        {
            key: 'viewedAt',
            header: t('search.history.table.headers.date'),
            cell: (h) => formatDate(h.viewedAt),
            sortValue: (h) => new Date(h.viewedAt).getTime(),
            className: 'text-muted-foreground',
        },
    ];

    return (
        <div className="container mx-auto max-w-6xl px-4 py-8">
            <div className="mb-6">
                <h1 className="text-2xl font-bold">
                    {t('search.history.title')}
                </h1>
                <p className="mt-1 text-sm text-muted-foreground">
                    {t('search.history.subtitle')}
                </p>
            </div>

            {isLoading ? (
                <div className="flex justify-center py-16">
                    <LoadingRing size="large" />
                </div>
            ) : history.length === 0 ? (
                <div className="mt-4 flex flex-col items-center justify-center rounded-lg border border-dashed py-16 text-center">
                    <HistoryIcon className="mb-3 h-10 w-10 text-muted-foreground" />
                    <h2 className="mb-1 text-lg font-semibold">
                        {t('search.history.noSearches.title')}
                    </h2>
                </div>
            ) : (
                <>
                    <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
                        <div className="relative w-full sm:max-w-md">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder={t(
                                    'search.history.filters.searchPlaceholder'
                                )}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-9"
                            />
                        </div>
                        <MultiCombobox
                            options={allTypes}
                            value={selectedTypes}
                            onChange={setSelectedTypes}
                            placeholder={t(
                                'search.history.filters.typesPlaceholder'
                            )}
                            searchPlaceholder={t(
                                'search.history.filters.typesSearchPlaceholder'
                            )}
                            emptyText={t('search.history.filters.noTypes')}
                            className="w-full sm:w-56"
                            aria-label="Filter by type"
                        />
                        <MultiCombobox
                            options={allManufacturers}
                            value={selectedManufacturers}
                            onChange={setSelectedManufacturers}
                            placeholder={t(
                                'search.history.filters.manufacturersPlaceholder'
                            )}
                            searchPlaceholder={t(
                                'search.history.filters.manufacturersSearchPlaceholder'
                            )}
                            emptyText={t(
                                'search.history.filters.noManufacturers'
                            )}
                            className="w-full sm:w-56"
                            aria-label="Filter by manufacturer"
                        />
                    </div>

                    {visibleHistory.length === 0 ? (
                        <div className="mt-4 flex flex-col items-center justify-center rounded-lg border border-dashed py-16 text-center">
                            <Search className="mb-3 h-10 w-10 text-muted-foreground" />
                            <p className="max-w-md text-sm text-muted-foreground">
                                {t('search.history.filters.noResults')}
                            </p>
                        </div>
                    ) : (
                        <DataTable
                            columns={columns}
                            items={visibleHistory}
                            rowKey={(h) => h._id}
                            onRowClick={(h) =>
                                navigate('/implant/' + h.implantId)
                            }
                            pageSize={20}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default History;
